/*
 * Further: photo collage / gallery.
 *
 * Ported from the inline script of the static site's collage.html: masonry
 * grid with reveal-on-scroll, hover glow, lightbox, shuffle and fullscreen.
 * Loaded by sections/further-gallery.liquid.
 *
 * What changed in the port:
 *  - The photos come from the section's blocks (rendered by Liquid) instead of
 *    the old /api/images feed, so the grid is in the page before this runs.
 *  - GSAP / ScrollTrigger are no longer loaded. The page only used them to tag
 *    every fourth photo for the red/cyan hover tint on first load; that is one
 *    line here.
 *  - First reveal. On the static page the thumbnails had no size until they
 *    loaded, so for a first-time visitor every photo sat inside the window at
 *    once and they all faded in right away, 50ms apart. Here the grid keeps
 *    its shape while loading, so that cascade is done on purpose ("Photo
 *    reveal: all photos fade in when the page opens"). The other setting
 *    fades each photo in as it scrolls into view. After a shuffle both
 *    behave like the original: photos fade in as they come into view.
 */
(function () {
  'use strict';

  if (window.FurtherGallery) return;
  window.FurtherGallery = true;

  // ================================
  // State
  // ================================
  var currentLightboxIndex = 0;
  var loadedImages = [];
  var lightboxTrigger = null;

  var masonryGrid = null;
  var lightbox = null;
  var lightboxImage = null;
  var lightboxClose = null;
  var lightboxPrev = null;
  var lightboxNext = null;

  // ================================
  // Masonry Grid
  // ================================
  function readItem(item, index) {
    var img = item.querySelector('img');
    var editorAttributes = [];
    for (var i = 0; i < item.attributes.length; i++) {
      var attr = item.attributes[i];
      if (attr.name.indexOf('data-shopify') === 0) editorAttributes.push([attr.name, attr.value]);
    }
    return {
      src: item.getAttribute('data-full') || (img ? img.currentSrc || img.src : ''),
      srcset: item.getAttribute('data-full-srcset') || '',
      width: parseInt(item.getAttribute('data-width'), 10) || 0,
      height: parseInt(item.getAttribute('data-height'), 10) || 0,
      thumb: img ? img.getAttribute('src') : '',
      thumbSrcset: img ? img.getAttribute('srcset') || '' : '',
      thumbSizes: img ? img.getAttribute('sizes') || '' : '',
      thumbWidth: img ? img.getAttribute('width') : null,
      thumbHeight: img ? img.getAttribute('height') : null,
      alt: (img && img.getAttribute('alt')) || 'Gallery photo ' + (index + 1),
      editorAttributes: editorAttributes
    };
  }

  function bindItem(item, index) {
    item.dataset.index = index;

    // Track mouse position for glow effect
    item.addEventListener('mousemove', function (e) {
      var rect = item.getBoundingClientRect();
      var x = ((e.clientX - rect.left) / rect.width) * 100;
      var y = ((e.clientY - rect.top) / rect.height) * 100;
      item.style.setProperty('--mouse-x', x + '%');
      item.style.setProperty('--mouse-y', y + '%');
    });

    // Lightbox click
    item.addEventListener('click', function () {
      openLightbox(index);
    });

    // Keyboard activation
    item.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openLightbox(index);
      }
    });
  }

  function createCollageItem(image, index) {
    var item = document.createElement('div');
    item.className = 'collage-item';
    item.setAttribute('role', 'button');
    item.setAttribute('tabindex', '0');
    item.setAttribute('aria-label', 'Open image: ' + (image.alt || 'Gallery photo ' + (index + 1)));
    item.setAttribute('data-full', image.src);
    if (image.srcset) item.setAttribute('data-full-srcset', image.srcset);
    if (image.width) item.setAttribute('data-width', image.width);
    if (image.height) item.setAttribute('data-height', image.height);
    image.editorAttributes.forEach(function (pair) {
      item.setAttribute(pair[0], pair[1]);
    });

    var img = document.createElement('img');
    if (image.thumbSizes) img.sizes = image.thumbSizes;
    if (image.thumbSrcset) img.srcset = image.thumbSrcset;
    img.src = image.thumb;
    img.alt = image.alt;
    if (image.thumbWidth) img.setAttribute('width', image.thumbWidth);
    if (image.thumbHeight) img.setAttribute('height', image.thumbHeight);
    img.loading = 'lazy';

    item.appendChild(img);
    bindItem(item, index);

    return item;
  }

  function renderGrid(images) {
    masonryGrid.innerHTML = '';
    loadedImages = images;

    images.forEach(function (image, index) {
      var item = createCollageItem(image, index);
      masonryGrid.appendChild(item);
    });

    // Observe items for lazy animation
    observeItems();
  }

  function shuffleArray(array) {
    var newArray = array.slice();
    for (var i = newArray.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var tmp = newArray[i];
      newArray[i] = newArray[j];
      newArray[j] = tmp;
    }
    return newArray;
  }

  // ================================
  // Intersection Observer for Lazy Animation
  // ================================
  function observeItems() {
    var items = masonryGrid.querySelectorAll('.collage-item');

    if (!('IntersectionObserver' in window)) {
      items.forEach(function (item) {
        item.classList.add('visible');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry, i) {
          if (entry.isIntersecting) {
            setTimeout(function () {
              entry.target.classList.add('visible');
            }, i * 50);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '50px'
      }
    );

    items.forEach(function (item) {
      observer.observe(item);
    });
  }

  // ================================
  // Lightbox
  // ================================
  function showInLightbox(index) {
    var img = loadedImages[index];
    if (!img) return;

    if (img.srcset) {
      // The picture is limited by both 90vw and 84vh: tell the browser how
      // wide it will really be so it picks a rendition that is sharp, not huge.
      var maxWidth = window.innerWidth * 0.9;
      var maxHeight = window.innerHeight * 0.84;
      var shown = img.width && img.height ? Math.min(maxWidth, (maxHeight * img.width) / img.height) : maxWidth;
      lightboxImage.sizes = Math.max(1, Math.round(shown)) + 'px';
      lightboxImage.srcset = img.srcset;
    } else {
      lightboxImage.removeAttribute('srcset');
      lightboxImage.removeAttribute('sizes');
    }
    lightboxImage.src = img.src;
    lightboxImage.alt = img.alt;
  }

  function openLightbox(index) {
    if (!lightbox) return;
    lightboxTrigger = document.activeElement;
    currentLightboxIndex = index;
    showInLightbox(index);
    lightbox.classList.add('active');
    document.body.style.overflow = 'hidden';
    lightboxClose.focus();
  }

  function closeLightbox() {
    lightbox.classList.remove('active');
    document.body.style.overflow = '';
    if (lightboxTrigger) {
      lightboxTrigger.focus();
      lightboxTrigger = null;
    }
  }

  function navigateLightbox(direction) {
    if (!loadedImages.length) return;
    currentLightboxIndex = (currentLightboxIndex + direction + loadedImages.length) % loadedImages.length;
    showInLightbox(currentLightboxIndex);
  }

  // Keyboard navigation
  document.addEventListener('keydown', function (e) {
    if (!lightbox || !lightbox.classList.contains('active')) return;
    if (e.key === 'Escape') closeLightbox();
    if (e.key === 'ArrowLeft') navigateLightbox(-1);
    if (e.key === 'ArrowRight') navigateLightbox(1);

    // Focus trap within lightbox
    if (e.key === 'Tab') {
      var focusable = [lightboxClose, lightboxPrev, lightboxNext];
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
  });

  // ================================
  // Initialization
  // ================================
  function init() {
    var grid = document.querySelector('[data-further-gallery]');
    if (!grid || grid.getAttribute('data-further-gallery') === 'ready') return;
    grid.setAttribute('data-further-gallery', 'ready');

    masonryGrid = grid;
    lightbox = document.getElementById('lightbox');
    lightboxImage = document.getElementById('lightbox-image');
    lightboxClose = document.getElementById('lightbox-close');
    lightboxPrev = document.getElementById('lightbox-prev');
    lightboxNext = document.getElementById('lightbox-next');

    // The photos are already in the page (rendered by the section): take them over.
    var items = Array.prototype.slice.call(masonryGrid.querySelectorAll('.collage-item'));
    loadedImages = items.map(readItem);
    items.forEach(function (item, index) {
      bindItem(item, index);
      // RGB split hover tint on every fourth photo of the first layout
      if (index % 4 === 0) item.classList.add('effect-rgb');
    });

    if (masonryGrid.getAttribute('data-reveal') === 'scroll') {
      observeItems();
    } else {
      // all photos fade in when the page opens, 50ms apart, in order
      items.forEach(function (item, index) {
        setTimeout(function () {
          item.classList.add('visible');
        }, index * 50);
      });
    }

    if (lightbox) {
      lightboxClose.addEventListener('click', closeLightbox);
      lightboxPrev.addEventListener('click', function () {
        navigateLightbox(-1);
      });
      lightboxNext.addEventListener('click', function () {
        navigateLightbox(1);
      });
      lightbox.addEventListener('click', function (e) {
        if (e.target === lightbox) closeLightbox();
      });
    }

    // ================================
    // Controls
    // ================================
    var shuffleBtn = document.getElementById('shuffle-btn');
    var fullscreenBtn = document.getElementById('fullscreen-btn');

    if (shuffleBtn) {
      shuffleBtn.addEventListener('click', function () {
        var shuffled = shuffleArray(loadedImages);

        // Animate out
        masonryGrid.querySelectorAll('.collage-item').forEach(function (item) {
          item.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
          item.style.opacity = '0';
          item.style.transform = 'scale(0.8)';
        });

        setTimeout(function () {
          renderGrid(shuffled);
        }, 400);
      });
    }

    if (fullscreenBtn) {
      var root = document.documentElement;
      if (!(root.requestFullscreen || root.webkitRequestFullscreen) || !(document.exitFullscreen || document.webkitExitFullscreen)) {
        // iPhone Safari has no fullscreen for web pages: no button rather than one that does nothing
        fullscreenBtn.style.display = 'none';
      } else {
        fullscreenBtn.addEventListener('click', function () {
          var current = document.fullscreenElement || document.webkitFullscreenElement;
          var enter = root.requestFullscreen || root.webkitRequestFullscreen;
          var leave = document.exitFullscreen || document.webkitExitFullscreen;
          var result = current ? leave.call(document) : enter.call(root);
          if (result && typeof result.catch === 'function') {
            result.catch(function () {
              /* refused by the browser: nothing to do */
            });
          }
        });
      }
    }
  }

  // Start when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: the section was added or re-rendered, or a photo block was picked
  document.addEventListener('shopify:section:load', function () {
    init();
  });
  document.addEventListener('shopify:block:select', function (e) {
    var item = e.target;
    if (item && item.classList && item.classList.contains('collage-item')) {
      item.classList.add('visible');
      item.scrollIntoView({ block: 'center' });
    }
  });
})();
